import uniq from 'lodash/uniq';
import uniqWith from 'lodash/uniqWith';

import { FUTURE, PAST_YEAR, PRESENT } from 'timeline/enums/constants';
import { getTimelineTimePeriod } from 'timeline/utils/getTimelineTimePeriod';
import { FILTER_ENUMS } from 'search/enums/constants';

export const getTimelinePresentFutureSortOptions = (sortOptions) => {
  const sortOptionsToShowDisplayName = new Set(['Popular', 'New In', 'Upcoming Releases']);

  return sortOptions.filter((sortOption) => {
    return sortOptionsToShowDisplayName.has(sortOption.display_name);
  });
};

export const getTimelinePastSortOptions = (sortOptions) => {
  const sortOptionsToShowDisplayName = new Set(['Popular', 'Upcoming Releases', 'Date']);

  const filteredSortOptions = sortOptions.filter((sortOption) =>
    sortOptionsToShowDisplayName.has(sortOption.display_name),
  );

  return filteredSortOptions.map((sortOption) => {
    if (sortOption.display_name === 'Upcoming Releases') {
      sortOption.display_name = 'Date';
    }
    return sortOption;
  });
};

const getSortOptionsArr = (sortOptions, globalPricingId: string) => {
  // checks if the price sort for the global pricing region exists.
  // For example: if the globalPricingId is 420, but we do not have `gp_lowest_price_cents_420`
  // we want to sort by `lowest_price_cents`
  const globalPricingSortExists =
    !!globalPricingId &&
    sortOptions.find((option) => option.sort_by === `gp_lowest_price_cents_${globalPricingId}`);

  return sortOptions.filter((option) => {
    const currentSortBy = option?.sort_by;

    // remove any options that aren't Popular from sort options (such as Upcoming Releases and New In)
    // allow recently_released facets (New In Apparel and Recently Released Sneakers)
    if (['relevance'].includes(currentSortBy) || ['apparel', 'sneakers'].includes(option.value)) {
      return true;
    }

    if (globalPricingSortExists && currentSortBy === `gp_lowest_price_cents_${globalPricingId}`) {
      return true;
    }

    if (!globalPricingSortExists && currentSortBy === 'lowest_price_cents') {
      return true;
    }

    return false;
  });
};

// FIXME: for some reason the value of `sortOptions` is non-deterministic i.e. it will sometimes
// contain duplicated items and this was causing React hydration errors. Deduping fixes the issue
// but it's better to fix this upstream i.e. track down & eliminate the source of inconsistency
const dedupeIncomingOptions = (sortOptions) => {
  const stringified = sortOptions.map((opt) => JSON.stringify(opt));

  const deduper = (a, b) => {
    const strA = stringified[sortOptions.indexOf(a)];
    const strB = stringified[sortOptions.indexOf(b)];
    return strA === strB;
  };

  return uniqWith(sortOptions, deduper);
};

export const getSortOptions = ({
  sortOptions,
  facets,
  globalPricingId,
  router: { pathname, asPath, query },
}) => {
  const timelineTimePeriod = getTimelineTimePeriod({ pathname, asPath, query });

  // timeline only shows Popular, New In, and Upcoming Releases sort options for past and future
  if (timelineTimePeriod === FUTURE || timelineTimePeriod === PRESENT) {
    return getTimelinePresentFutureSortOptions(sortOptions);
  }

  // timeline only shows Popular, and Upcoming Releases (renamed as Date) for past years
  if (timelineTimePeriod === PAST_YEAR) {
    return getTimelinePastSortOptions(sortOptions);
  }

  // insert recently_released facet: New In Apparel and Recently Released Sneakers, as the 2nd and 3rd sorts into sort options
  const recentlyReleased = facets?.find((facet) => facet?.name === FILTER_ENUMS.RECENTLY_RELEASED);
  const hasRecentlyReleased = sortOptions.find(
    (option) => option.count && option.value === 'sneakers',
  );
  if (recentlyReleased && !hasRecentlyReleased) {
    const recentlyReleasedOptions = recentlyReleased?.options;
    sortOptions.splice(1, 0, ...recentlyReleasedOptions);

    // if recently_released facet is selected, set Popular status as not 'selected'
    // so that it does not show up checked in the UI
    if (recentlyReleasedOptions?.some((facet) => facet?.status === 'selected')) {
      const popularSort = sortOptions?.find((option) => option?.sort_by === 'relevance');
      const updatedPopularSort = { ...popularSort, status: '' };
      if (popularSort) {
        // replace Popular sort with updatedPopularSort
        const indexOfPopularSort = sortOptions
          ?.map((option) => option?.sort_by)
          ?.indexOf('relevance');
        if (typeof indexOfPopularSort === 'number') {
          sortOptions.splice(indexOfPopularSort, 1, updatedPopularSort);
        }
      }
    }
  }

  const dedupedSortOptions = dedupeIncomingOptions(sortOptions);

  return uniq(getSortOptionsArr(dedupedSortOptions, globalPricingId));
};
